import gql from 'graphql-tag';
import React, {ReactNode} from 'react';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {MeasurementUnitFragment} from 'constants/MeasurementConstants';
import {InaccessibleInfoNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {isVolumeOrNullUnit} from 'inventory/InventoryUnitUtil';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {FillPlateStructuredTableItemsQuery} from 'samples/plate-maps/fill-from-structured-table-modal/__generated/FillPlateStructuredTableItemsQuery';
import {FillPlatePlannedValuesQuery} from 'samples/plate-maps/fill-plate-modal/__generated/FillPlatePlannedValuesQuery';
import {FillPlateWorklistItemsQuery} from 'samples/plate-maps/fill-plate-modal/__generated/FillPlateWorklistItemsQuery';
import {
  FILL_PLATE_BIOENTITY_WITH_TEST_ORDERS_FRAGMENT,
  maybeHydrateBioentitySourceWithTestOrders,
  maybeHydrateContainerSourceWithTestOrders,
  ProcedureTestOrderConfig,
  validateSourceTestOrders,
} from 'samples/plate-maps/integrations/procedures/PlateMapTestOrderUtils';
import {ReagentType, Source, TestOrderInfoById} from 'samples/plate-maps/PlateMapConstants';
import {validateContainerForFill} from 'samples/plate-maps/PlateMapUtil';
import {
  getDefaultMeasurementValidationErrors,
  getDefaultMeasurementWithErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import ExpirationDateCell from 'samples/valid-contents/ExpirationDateCell';
import {EXPIRATION_DATE_FIELD_DISPLAY_NAME} from 'samples/valid-contents/ValidContentsConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {ListItemValidation, SelectItemsAdditionalColumn} from 'worklists/SelectItemsFromListView';

export const FILL_PLATE_LIST_ITEM_CONTAINER_FRAGMENT = gql`
  fragment FillPlateListItemContainerFragment on ContainerNode {
    id
    name
    archiveRecord {
      purpose
    }
    permissions {
      readable
      writable
      appendable
      admin
    }
    storableSchema {
      id
      name
      iconId
    }
    contents {
      ... on ContainerContentNode {
        id
        concentration {
          value
          unit {
            ...MeasurementUnit
          }
        }
        containedItem {
          ... on RegistryItemInterface {
            ...FillPlateBioentityWithTestOrdersFragment
          }
          ...InaccessibleInfoGraphQL
        }
        containedItemTagSchema {
          ... on TagSchemaNode {
            id
            name
            iconId
          }
        }
      }
    }
    parentStorable {
      ... on StorableInterface {
        id
        name
        storableSchema {
          id
          iconId
          ... on PlateSchemaNode {
            id
            plateSchemaType
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    barcode {
      value
    }
    quantity {
      value
      unit {
        ...MeasurementUnit
      }
    }
    checkOutStatusInfo: checkOutStatusInfoJson
    sampleRoles {
      ...SampleRolesGraphQL
    }
    restrictionStatus
    expirationInfo {
      id
      isExpired
      isTrackingEnabled
      expirationDate
    }
  }
  ${getFragmentWrapper('SampleRoles').fragment}
  ${MeasurementUnitFragment.fragment}
  ${FILL_PLATE_BIOENTITY_WITH_TEST_ORDERS_FRAGMENT}
`;

export const FILL_PLATE_LIST_ITEM_BIOENTITY_FRAGMENT = gql`
  fragment FillPlateListItemBioentityFragment on RegistryItemInterface {
    ... on RegistryItemInterface {
      id
      name
      archiveRecord {
        purpose
      }
      tagSchema {
        id
        isContainable
        name
        iconId
      }
      ...FillPlateBioentityWithTestOrdersFragment
    }
  }
  ${FILL_PLATE_BIOENTITY_WITH_TEST_ORDERS_FRAGMENT}
`;

export type WorklistItemNode = ExcludeByTypename<
  ExcludeInaccessible<FillPlateWorklistItemsQuery['worklist']>['items'][number],
  'BatchNode' | 'PlateNode'
>;
type WorklistContainedItemNode = ExtractByTypename<
  WorklistItemNode,
  'ContainerNode'
>['contents'][number]['containedItem'];

export type PlannedValueItemNode = FillPlatePlannedValuesQuery['bioentities'][number];

export type StructuredTableBioentityNode = FillPlateStructuredTableItemsQuery['bioentities'][number];
export type StructuredTableContainerNode = FillPlateStructuredTableItemsQuery['containers'][number];
export type StructuredTableItemNode = StructuredTableBioentityNode | StructuredTableContainerNode;
export type AccessibleStructuredTableItemNode = ExcludeInaccessible<StructuredTableItemNode>;

export type ItemNode = WorklistItemNode | PlannedValueItemNode | StructuredTableItemNode;

export function validateFillPlateListItem({
  listItemNode,
  destinationPlateId,
  procedureTestOrderConfig,
  plateMapType,
}: {
  listItemNode: ItemNode;
  destinationPlateId: string | null;
  procedureTestOrderConfig: ProcedureTestOrderConfig | null;
  plateMapType: PlateMapType;
}): ListItemValidation {
  let isSelectorDisabled = false;
  let tooltipText;
  if (isInaccessible(listItemNode)) {
    isSelectorDisabled = true;
    tooltipText = "You don't have permissions to view this item.";
  } else if (isArchived(listItemNode)) {
    isSelectorDisabled = true;
    tooltipText = 'This item is archived.';
  } else if (listItemNode.__typename === 'ContainerNode') {
    // Used to check if the container is part of the well being transferred into. If it's inaccessible,
    //  we can assume it's not the same as the plate.
    const parentStorableId =
      listItemNode.parentStorable != null && !isInaccessible(listItemNode.parentStorable)
        ? listItemNode.parentStorable.id
        : null;
    const containedItemNodes: Array<WorklistContainedItemNode> = listItemNode.contents.map(
      ({containedItem}) => containedItem
    );
    const inaccessibleContainedItems: Array<InaccessibleInfoNode> = containedItemNodes.filter(
      (containedItemNode): containedItemNode is InaccessibleInfoNode => isInaccessible(containedItemNode)
    );
    let warning = validateContainerForFill(
      {
        ...listItemNode,
        containedItemIds: containedItemNodes.map((containedItem) =>
          getAccessibleOrInaccessibleId(containedItem)
        ),
        inaccessibleContentIds: inaccessibleContainedItems.map(({invalidId}) => invalidId),
        parentStorableId,
      },
      destinationPlateId
    );
    if (!warning && !isVolumeOrNullUnit(listItemNode.quantity.unit)) {
      warning = 'This container has invalid units.';
    }
    if (!warning) {
      warning = validateSourceTestOrders({item: listItemNode, procedureTestOrderConfig, plateMapType});
    }
    if (warning != null) {
      tooltipText = warning;
      isSelectorDisabled = true;
    }
  } else if (!(listItemNode.tagSchema?.isContainable ?? false)) {
    isSelectorDisabled = true;
    tooltipText = 'This item is not containable.';
  } else {
    const warning = validateSourceTestOrders({item: listItemNode, procedureTestOrderConfig, plateMapType});
    if (warning != null) {
      tooltipText = warning;
      isSelectorDisabled = true;
    }
  }
  return {
    isSelectorDisabled,
    tooltipText,
  };
}

export function getSourcesFromQueryItems({
  items,
  materialInputId,
  procedureTestOrderConfig,
  plateMapType,
}: {
  items: Array<ExcludeInaccessible<ItemNode>>;
  materialInputId?: string;
  procedureTestOrderConfig: ProcedureTestOrderConfig | null;
  plateMapType: PlateMapType;
}): {
  sources: Array<Source>;
  schemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}};
  testOrderInfoById: TestOrderInfoById | null;
} {
  const sources: Array<Source> = [];
  const schemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}} = {};
  let testOrderInfoById: TestOrderInfoById = {};
  for (const item of items) {
    if (item.__typename === 'ContainerNode') {
      const source: Source = {
        type: ReagentType.SOURCE_CONTAINER,
        id: item.id,
        groupId: item.storableSchema.id,
        contents: [],
        transferQuantity: getDefaultMeasurementWithErrors(),
        quantity: item.quantity ? {value: item.quantity.value, unit: item.quantity.unit} : null,
      };
      if (
        item.parentStorable != null &&
        !isInaccessible(item.parentStorable) &&
        item.parentStorable.__typename === 'PlateNode'
      ) {
        source.parentPlateId = item.parentStorable.id;
      }
      if (item.contents.length === 0) {
        // If there's nothing in the container, keep track of the container schema
        schemaDataById[item.storableSchema.id] = {
          name: item.storableSchema.name,
          iconId: item.storableSchema.iconId as BenchlingIconName,
        };
      } else {
        // If there are one or more contents in the container, add them as reagents and keep track of
        // their schemas
        for (const content of item.contents) {
          source.contents.push({
            type: ReagentType.SOURCE_CONTAINABLE,
            id: getAccessibleOrInaccessibleId(content.containedItem),
            groupId: content.containedItemTagSchema.id,
            transferQuantity: getDefaultMeasurementWithErrors(),
            sourceConcentration: {
              measurement: {
                value: content.concentration?.value ?? null,
                unit: content.concentration?.unit ?? null,
              },
              errors: getDefaultMeasurementValidationErrors(),
            },
            finalConcentration: getDefaultMeasurementWithErrors(),
          });
          schemaDataById[content.containedItemTagSchema.id] = {
            name: content.containedItemTagSchema.name,
            iconId: content.containedItemTagSchema.iconId as BenchlingIconName,
          };
        }
      }
      const testOrderResult = maybeHydrateContainerSourceWithTestOrders({
        containerWithTestOrders: item,
        baseReagent: source,
        procedureTestOrderConfig,
        plateMapType,
      });
      testOrderInfoById = {...testOrderInfoById, ...testOrderResult.testOrderInfoById};
      sources.push(...testOrderResult.reagentsWithTestOrders);
      assert(testOrderResult.sourceError == null, 'Expected to receive only valid test orders from modal', {
        sourceError: testOrderResult.sourceError,
      });
    } else {
      const tagSchema = item.tagSchema!;
      // We shouldn't be able to select anything that isn't containable (and by extension schematized).
      const source: Source = {
        type: ReagentType.SOURCE_CONTAINABLE,
        id: item.id,
        groupId: tagSchema.id,
        transferQuantity: getDefaultMeasurementWithErrors(),
        sourceConcentration: getDefaultMeasurementWithErrors(),
        finalConcentration: getDefaultMeasurementWithErrors(),
      };
      if (materialInputId != null) {
        source.materialInput = {materialInputId, error: null};
      }
      const testOrderResult = maybeHydrateBioentitySourceWithTestOrders({
        entity: item,
        baseReagent: source,
        procedureTestOrderConfig,
        plateMapType,
      });
      testOrderInfoById = {...testOrderInfoById, ...testOrderResult.testOrderInfoById};
      sources.push(...testOrderResult.reagentsWithTestOrders);
      assert(testOrderResult.sourceError == null, 'Expected to receive only valid test orders from modal', {
        sourceError: testOrderResult.sourceError,
      });
      schemaDataById[tagSchema.id] = {
        name: tagSchema.name,
        iconId: tagSchema.iconId as BenchlingIconName,
      };
    }
  }
  return {
    sources,
    schemaDataById,
    testOrderInfoById: procedureTestOrderConfig != null ? testOrderInfoById : null,
  };
}

function getExpirationDateCell(itemNode: WorklistItemNode): ReactNode {
  if (itemNode.__typename === 'ContainerNode') {
    return <ExpirationDateCell expirationInfo={itemNode.expirationInfo} />;
  } else if (itemNode.__typename !== 'InaccessibleInfoNode') {
    const expirationDate = itemNode.hasExpirationTracking
      ? itemNode.tagsJson.find((tag) => tag.tagSchemaField.name === EXPIRATION_DATE_FIELD_DISPLAY_NAME)?.value
      : null;
    const expirationInfo = {isTrackingEnabled: itemNode.hasExpirationTracking, expirationDate};
    return <ExpirationDateCell expirationInfo={expirationInfo} />;
  } else {
    return 'N/A';
  }
}

export function getAdditionalTableColumns(): Array<SelectItemsAdditionalColumn<WorklistItemNode>> {
  const expirationDateColumn: ScrollableTableColumn = {
    header: 'Expiration date',
    accessorKey: 'expirationDate',
    isCollapsible: false,
    isSortable: false,
  };
  return BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE
    ? [{column: expirationDateColumn, accessor: getExpirationDateCell}]
    : [];
}
